import type { BaseHttpRequest } from '@/api/generated-client/core/BaseHttpRequest';
import { BaseApi } from '@/api/base-api';

export type TagEntity = 'game';
export type TagKind = 'ui_badge' | 'internal';
export type TagSchemaType = 'bool' | 'int' | 'string' | 'image';
export type TagAssignmentMode = 'auto' | 'manual' | 'both';
export type TagState = 'enabled' | 'disabled';
export type TagSortBy = 'id' | 'slug' | 'kind' | 'state' | 'createdAt';
export type SortDirection  = 'ASC' | 'DESC';

export type TagValue = {
  imageUrl: string;
  position: number;
}

export type TagRaw = {
  id: string;
  slug: string;
  entity: TagEntity;
  kind: TagKind;
  schemaType: TagSchemaType;
  assignmentMode: TagAssignmentMode;
  state: TagState;
  value: TagValue;
  createdAt: string;
  updatedAt: string;
}

export type PaginatedTagsResponse = {
  items: TagRaw[];
  total: number;
  page?: number;
  size?: number;
}

export type TagCreateRequest = {
  slug: string;
  entity: TagEntity;
  kind: TagKind;
  schemaType: TagSchemaType;
  assignmentMode: TagAssignmentMode;
  state: TagState;
  value: TagValue;
}

export type TagUpdateRequest = Pick<TagCreateRequest, 'slug' | 'state' | 'value'>;

export type GetTagsQuery = {
  pageNumber?: number;
  pageSize?: number;
  pageSortDirection?: SortDirection;
  pageSortBy?: TagSortBy;
  entity?: TagEntity;
  kind?: TagKind;
  state?: TagState;
  assignmentMode?: Array<TagAssignmentMode>;
  tagSlug?: string;
}

export class TagsApi extends BaseApi {
  private controller = '/common/catalog';
  private tagController = '/catalog/tags';

  constructor(public readonly httpRequest: BaseHttpRequest) {
    super();
  }

  public async uploadTagImage({ file }: { file: File }): Promise<string> {
    return await this.httpRequest.rpcRequest(
      this.tagController,
      '[POST] /api/v1/tags/image',
      {},
      { formData: { file } }
    );
  }

  public async getTags({ page, size, state }: { page?: number, size?: number, state?: TagState } ): Promise<PaginatedTagsResponse> {
    const pageQuery = page && page > 0 ? `&page.number=${page}` : '';
    const sizeQuery = size && size > 0 ? `&page.size=${size}` : '';
    const stateQuery = state && state.length > 0 ? `&state=${state}` : '';
    
    return await this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/tags?entity=game&kind=ui_badge&page.sortBy=position${pageQuery}${sizeQuery}${stateQuery}`,
      {}
    );
  }

  public async createTag(payload: TagCreateRequest): Promise<TagRaw> {
    return await this.httpRequest.rpcRequest(
      this.controller,
      '[POST] /api/v1/tags',
      payload
    );
  }

  public getTag(tagId: string): Promise<TagRaw> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[GET] /api/v1/tags/${tagId}`,
      {}
    );
  }

  public updateTag(tagId: string, payload: TagUpdateRequest): Promise<TagRaw> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[PATCH] /api/v1/tags/${tagId}`,
      payload
    );
  }

  public deleteTag(tagId: string): Promise<void> {
    return this.httpRequest.rpcRequest(
      this.controller,
      `[DELETE] /api/v1/tags/${tagId}`,
      {}
    );
  }
}
